import { CancelOutlined } from "@mui/icons-material";
import { Alert, FormControl, FormHelperText } from "@mui/material";
import { useState } from "react";
import { useEffect } from "react";
import { useRef } from "react";
import { Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import TextField from "@mui/material/TextField";
import EventListenerModel from "utils/EventListenerModel";
import { devLog } from "utils/devLog";

/**
 * @typedef {Object} SearchTypeInputDataHostData
 */

/**
 * @typedef {Object} SearchTypeDataHostData
 * @property {string} label
 * @property {string} value
 * @property {string} type
 * @property {string} placeholder
 * @property {string} value
 * @property {string} validationErrorMessage
 * @property {function} onValidate
 */

/**
 * @typedef {Object} MemberSearchDataHostData
 * @property {string} label
 * @property {string} key
 * @property {SearchTypeDataHostData[]} searchTypes
 */

const searchTypeMap = new Map();

/**
 ** inputs layer
 */
class SearchTypeInputDataHost extends EventListenerModel {
  inputDataHost = undefined;
  label = "";
  key = "";
  type = "radio";
  placeholder = "";
  value = undefined;
  onValidate = undefined;
  error = false;
  validationErrorMessage = {};

  constructor(input = {}, inputDataHost) {
    super();
    this.inputDataHost = inputDataHost;
    this.label = input.label;
    this.key = input.key;
    this.type = input.type;
    this.placeholder = input.placeholder;
    this.value = input.value;
    this.error = input.error;
    this.onValidate = input.onValidate;
    this.validationErrorMessage = input.validationErrorMessage;
    // this.inputParams = input.inputParams || {};
  }

  setValue(val) {
    devLog("val: ", val);
    this.value = val;
    this.processEventListener("onValueChange", this.value);
  }

  validate() {
    const isValid = typeof this.onValidate === "function" ? this.onValidate(this.value) : false;

    devLog("SearchTypeInputDataHost isValid: ", isValid);

    const oldError = this.error;
    this.error = !isValid;

    this.processEventListener("onValidate", this.value);
    if (oldError !== this.error) {
      // Only emit if the error status actually changed
      this.processEventListener("onValidateStatusChange", this.error); // Emit the new error status
    }
    devLog("ServiceTypeOptionHost (" + this.label + ") isValid: ", isValid);
    return isValid;
  }
}

/**
 *  @class
 *  @param {Object} searchTypeData
 * * searchType Layer
 */
class SearchTypeDataHost extends EventListenerModel {
  dataHost = undefined;
  label = "";
  key = "";
  selected = false;
  inputs = [];

  /**
   * @param {SearchTypeDataHostData} SearchTypeDataHostData
   */
  constructor(searchTypeData = {}, dataHost) {
    super();
    this.dataHost = dataHost;
    this.label = searchTypeData.label || "";
    this.key = searchTypeData.key || "";
    this.selected = searchTypeData.selected;
    this.inputs = (searchTypeData.inputs || []).map((input) => {
      return new SearchTypeInputDataHost(input, this);
    });
  }

  setSelected(type) {
    devLog("type: ", type);
    const oldType = this.selected;
    devLog("oldType: ", oldType);

    if (!!type === oldType) {
      return;
    }

    this.selected = !!type;
    this.processEventListener("onSelectedChanged", this.selected);

    if (!this.selected) {
      return;
    }
    this.dataHost.selected = this;
    this.dataHost.processEventListener("onSearchTypeSelected", this);
    for (let i = 0; i < this.dataHost.searchTypes.length; i++) {
      const searchType = this.dataHost.searchTypes[i];
      if (searchType === this) {
        continue;
      }
      searchType.inputs.map((input) => {
        input.error = false;
      });
      searchType.setSelected(false);
    }
  }

  validate() {
    let isValidated = true;
    for (let i = 0; i < this.inputs.length; i++) {
      const input = this.inputs[i];
      const isValid = input.validate();

      if (!isValid) {
        isValidated = false;
      }
    }
    devLog("return isValidated: ", isValidated);
    return isValidated;
  }
}

/**
 * A class for MemberSearchDataHost
 * @class
 * @param {...MemberSearchDataHostData} data - One to three {@link MemberSearchDataHostData} objects
 * * searchTypes Layer
 */
class MemberSearchDataHost extends EventListenerModel {
  searchTypes = [];
  selected = false;

  /**
   * @param {MemberSearchDataHostData} memberSearchData
   */
  constructor(searchTypes = []) {
    super();
    this.searchTypes = searchTypes.map((searchTypeData) => {
      // return new SearchTypeDataHost(searchTypeData, this);
      const newSearchType = new SearchTypeDataHost(searchTypeData, this);
      if (searchTypeData.selected) {
        this.selected = newSearchType;
      }
      return newSearchType;
    });

    if (!this.selected && this.searchTypes.length > 0) {
      this.searchTypes[0].setSelected(true);
    }
  }

  getData() {
    const data = {};

    if (this.selected) {
      data.searchType = this.selected.key;

      for (let k = 0; k < this.selected.inputs.length; k++) {
        const input = this.selected.inputs[k];
        data[input.key] = input.value;
      }
    }
    return data;
  }

  /**
   * This method validates the MemberSearchDataHost data structures
   * @returns boolean
   */
  validate() {
    let isValidated = true;
    for (let i = 0; i < this.searchTypes.length; i++) {
      const searchType = this.searchTypes[i];
      if (!searchType.selected) {
        continue;
      }

      isValidated = searchType.validate();
      devLog(`MemberSearchDataHost: ${isValidated}`);
    }
    return isValidated;
  }
}

/**
 * A component for rendering a member search input field
 *  *@param {Object} props - dataHost
 */
function SelectMemberSearchType(props) {
  const [dataHost, setDataHost] = useState(props.dataHost);
  const [searchTypes, setSearchTypes] = useState(props.dataHost.searchTypes || []);
  const [selectedSearchType, setSelectedSearchType] = useState(
    props.dataHost.selected || props.dataHost.searchTypes[0],
  );
  const [render, rerender] = useState(true);

  useEffect(() => {
    setDataHost(props.dataHost);
    setSearchTypes(props.dataHost.searchTypes);
    setSelectedSearchType(props.dataHost.selected || props.dataHost.searchTypes[0]);
  }, [props.dataHost]);

  const onSearchTypeAdded = (newType) => {
    setSearchTypes([...dataHost.searchTypes]);
  };

  const onSearchTypeSelected = (newType) => {
    setSelectedSearchType(newType);
    rerender(!render);
  };

  const onUserClick = (st) => {
    st.setSelected(st);
    setSelectedSearchType(st);
  };

  useEffect(() => {
    dataHost.addEventListener("onSearchTypeAdded", onSearchTypeAdded);
    dataHost.addEventListener("onSearchTypeSelected", onSearchTypeSelected);
    return () => {
      dataHost.removeEventListener("onSearchTypeAdded", onSearchTypeAdded);
      dataHost.removeEventListener("onSearchTypeSelected", onSearchTypeSelected);
    };
  }, [dataHost]);

  return (
    <>
      <Dropdown
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "10px",
        }}
      >
        <Dropdown.Toggle style={{ color: "white" }} variant="dark" id="dropdown-basic">
          {selectedSearchType.label}
        </Dropdown.Toggle>
        <Dropdown.Menu variant="dark">
          {searchTypes.map((searchType, index) => {
            return (
              <Dropdown.Item
                key={searchType.key}
                active={searchType.selected}
                onClick={(e) => {
                  devLog(e);
                  devLog("change search type to: ", searchType);
                  onUserClick(searchType);
                }}
              >
                {searchType.label}
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
      {selectedSearchType && <MemberSearchInputs searchTypeHost={selectedSearchType} />}
    </>
  );
}

function MemberSearchInputs(props) {
  const [searchTypeHost, setSearchTypeHost] = useState(props.searchTypeHost);
  const [inputs, setInputs] = useState(props.searchTypeHost.inputs);
  const [render, rerender] = useState(true);

  useEffect(() => {
    setSearchTypeHost(props.searchTypeHost);
    setInputs(props.searchTypeHost.inputs);
  }, [props.searchTypeHost]);

  const onSearchTypeSelected = (searchType) => {
    setSearchTypeHost(searchType);
    setInputs(searchType.inputs);
    rerender(!render);
  };

  useEffect(() => {
    searchTypeHost.dataHost.addEventListener("onSearchTypeSelected", onSearchTypeSelected);
    return () => {
      searchTypeHost.dataHost.removeEventListener("onSearchTypeSelected", onSearchTypeSelected);
    };
  }, [searchTypeHost]);

  return (
    <>
      {inputs.map((inputDataHost) => {
        devLog(inputDataHost.label);
        return <MemberSearchInput key={inputDataHost.key} inputDataHost={inputDataHost} />;
      })}
    </>
  );
}

function MemberSearchInput(props) {
  const [inputDataHost, setInputDataHost] = useState(props.inputDataHost);
  const [label, setLabel] = useState(props.inputDataHost.label);
  const [key, setKey] = useState(props.inputDataHost.key);
  const [type, setType] = useState(props.inputDataHost.type);
  const [value, setValue] = useState(props.inputDataHost.value || "");
  const [error, setError] = useState(props.inputDataHost.error);
  const [render, serRender] = useState(false);
  const [validationErrorMsg, setValidationErrorMsg] = useState(props.inputDataHost.validationErrorMessage);
  const [isValidated, setIsValidated] = useState(true);

  const { t } = useTranslation();

  useEffect(() => {
    devLog("MemberSearchInput: props.inputDataHost changed");
    setInputDataHost(props.inputDataHost);
    setLabel(props.inputDataHost.label);
    setKey(props.inputDataHost.key);
    setType(props.inputDataHost.type);
    setValue(props.inputDataHost.value || "");
    setError(props.inputDataHost.error);
    setValidationErrorMsg(props.inputDataHost.validationErrorMessage);
  }, [props.inputDataHost]);

  const onValueChange = (val) => {
    devLog(`onValueChange ${label} val: `, val);
    setValue(val);
    serRender(!render);
  };

  const onValidateStatusChange = (isError) => {
    setError(isError);
  };

  useEffect(() => {
    if (inputDataHost) {
      inputDataHost.addEventListener("onValueChange", onValueChange);
      inputDataHost.addEventListener("onValidateStatusChange", onValidateStatusChange);
    }
    return () => {
      if (inputDataHost) {
        inputDataHost.removeEventListener("onValueChange", onValueChange);
        inputDataHost.removeEventListener("onValidateStatusChange", onValidateStatusChange);
      }
    };
  }, [inputDataHost]);

  const memberNumberLimit = (e) => {
    if (key === "memberNumber" && e.target.value.length > 14) {
      e.target.value = e.target.value.slice(0, -1);
      devLog("member number Length error");
    }
    return e.target.value;
  };

  return (
    <>
      <FormControl
        error={error}
        style={{
          width: "100%",
          backgroundColor: "white",
          padding: 10,
          marginTop: 10,
        }}
      >
        <TextField
          id="outlined-basic-${inputDataHost.key}"
          label={label}
          variant="outlined"
          size="small"
          fullWidth={true}
          value={value}
          type={type}
          error={error}
          onChange={(e) => {
            // const processedValue = memberNumberLimit(e);
            devLog("target value change: ", e.target.value);
            inputDataHost.setValue(e.target.value);
          }}
        />
        {error && (
          <helperText style={{ fontSize: "13px", color: "red", marginLeft: 0 }}>❗{validationErrorMsg}</helperText>
        )}
      </FormControl>
    </>
  );
}

export default function CYSUMemberSearchForm(props) {
  const NAME = "NAME";
  const MEMBER_NUMBER = "MEMBER_NUMBER";

  const { t } = useTranslation();
  const formRef = useRef();
  const [firstName, setFirstName] = useState(props.currState.MemberLookup.firstName || props.currState.FirstName || "");
  const [lastName, setLastName] = useState(props.currState.MemberLookup.lastName || props.currState.LastName || "");
  const [phoneNumber, setPhoneNumber] = useState(
    props.currState.MemberLookup.phoneNumber || props.currState?.Number?.replace(/\D/g, "") || "",
  );
  const [memberNumber, setMemberNumber] = useState(
    props.currState.MemberLookup.memberNumber || props.currState.memberNumber || "",
  );

  const [selectedSearchType, setSelectedSearchType] = useState(props.currState.MemberLookup.searchType || "NAME");

  /**
   * @type {ReturnType<typeof useState<MemberSearchDataHost>>}
   */
  const [memberSearchData, setMemberSearchData] = useState(undefined);

  const searchTypesData = [];

  searchTypesData.push({
    label: t("FIMC.ContractSearch.DropdownOptions3"),
    key: "NAME",
    selected: selectedSearchType === NAME,
    inputs: [
      {
        label: t("FIMC.ContractSearch.DropdownSubOptions1"),
        key: "firstName",
        type: "string",
        placeholder: "Enter your first name here",
        value: firstName,
        validationErrorMessage: t("description.FirstNameRequiredMsg"),
        error: false,
        onValidate: (input) => {
          return input !== null && input !== "";
        },
      },
      {
        label: t("FIMC.ContractSearch.DropdownSubOptions2"),
        key: "lastName",
        type: "string",
        placeholder: "Enter your last name here",
        value: lastName,
        error: false,
        validationErrorMessage: t("description.LastNameRequiredMsg"),
        onValidate: (input) => {
          return input !== null && input !== "";
        },
      },
      {
        label: t("FIMC.ContractSearch.DropdownOptions2"),
        key: "phoneNumber",
        type: "string",
        placeholder: "Enter your phone number here",
        value: phoneNumber,
        error: false,
        validationErrorMessage: t("description.PhoneNumberRequiredMsg"),
        onValidate: (input) => {
          devLog("onValidate: ", input);
          return input !== null && input !== "";
        },
      },
    ],
  });

  searchTypesData.push({
    label: t("FIMC.ContractSearch.DropdownOptions1"),
    key: "MEMBER_NUMBER",
    selected: selectedSearchType === MEMBER_NUMBER,
    inputs: [
      {
        label: t("FIMC.ContractSearch.DropdownOptions1"),
        key: "memberNumber",
        type: "string",
        placeholder: "Enter your member number here",
        value: memberNumber,
        error: false,
        validationErrorMessage: t("description.MemberNumberRequiredMsg"),
        onValidate: (input) => {
          return input !== null && input !== "";
        },
      },
    ],
  });

  useEffect(() => {
    const st = new MemberSearchDataHost(searchTypesData);
    setMemberSearchData(st);
    devLog("searchType: ", st);
  }, []);

  useEffect(() => {
    setFirstName(firstName);
    setLastName(lastName);
    setPhoneNumber(phoneNumber);
    setMemberNumber(memberNumber);
  }, [selectedSearchType]);

  useEffect(() => {
    if (typeof props.onFormUpdated === "function") {
      props.onFormUpdated({
        firstName: firstName,
        lastName: lastName,
        phone: phoneNumber,
        memberNumber: memberNumber,
        searchType: selectedSearchType,
      });
    }
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    const data = memberSearchData.getData();
    const isValidated = memberSearchData.validate();

    if (!isValidated) {
      devLog("onSubmit details are invalid");
      return;
    }

    if (typeof props.onFormSubmit === "function") {
      if (data) {
        props.onFormSubmit(data);
      }
    }
  };

  const memberNumberLimit = (e) => {
    if (e.target.value.length > 14) {
      e.target.value = e.target.value.slice(0, -1);
      devLog("member number Length error");
    }
  };

  return (
    <>
      <div className="formContainer">
        <form ref={formRef} onSubmit={onSubmit} className="form">
          {memberSearchData && <SelectMemberSearchType dataHost={memberSearchData} />}

          <div className="footerContainer">
            <div>
              <button
                id="cancelButton"
                type="button"
                className="footer"
                style={{
                  background: "#f4f4f4",
                  color: "black",
                  paddingLeft: 14,
                }}
                onClick={(e) => {
                  e.preventDefault();
                  devLog("UI Cancel pressed");
                  if (typeof props.onUserCancel === "function") {
                    props.onUserCancel();
                  }
                }}
              >
                <CancelOutlined style={{ width: 20, color: "black", marginRight: 4 }} />
                <span style={{ marginLeft: "0px" }}>{t("description.CancelCallButton1")}</span>
              </button>
            </div>
            <button type="submit" className="ui-btn">
              {t("description.ConfirmCustomerSubmit")}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
